/**
 * Centralized password validation rules:
 * - 8 to 50 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - No spaces allowed
 */
export const validatePasswordComplexity = (password: string): string | null => {
    if (!password) {
        return "Password is required.";
    }
    if (password.length < 8) {
        return "Password must be at least 8 characters.";
    }
    if (password.length > 50) {
        return "Password must not exceed 50 characters.";
    }
    if (/\s/.test(password)) {
        return "Password cannot contain spaces.";
    }
    if (!/[A-Z]/.test(password)) {
        return "Password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(password)) {
        return "Password must contain at least one lowercase letter.";
    }
    if (!/[0-9]/.test(password)) {
        return "Password must contain at least one number.";
    }
    return null;
};

export interface PasswordStrength {
    /** 0 = empty, 1 = weak, 2 = fair, 3 = good, 4 = strong */
    score: 0 | 1 | 2 | 3 | 4;
    label: string;
    /** Progress bar fill (0-100). */
    percent: number;
    /** Bootstrap variant for the progress bar. */
    variant: 'danger' | 'warning' | 'info' | 'success';
}

/**
 * Lightweight heuristic password strength estimate for live UI feedback.
 * This is a usability hint only — the authoritative complexity rules live in
 * validatePasswordComplexity (and are re-enforced on the backend).
 */
export const calculatePasswordStrength = (password: string): PasswordStrength => {
    if (!password) {
        return { score: 0, label: '', percent: 0, variant: 'danger' };
    }

    let points = 0;

    // Length tiers.
    if (password.length >= 8) points++;
    if (password.length >= 12) points++;
    if (password.length >= 16) points++;

    // Character-class variety.
    if (/[a-z]/.test(password)) points++;
    if (/[A-Z]/.test(password)) points++;
    if (/[0-9]/.test(password)) points++;
    if (/[^A-Za-z0-9]/.test(password)) points++;

    // Penalize obvious low-entropy patterns.
    if (/^(.)\1+$/.test(password)) points = 1; // all same character
    if (/^[A-Za-z]+$/.test(password) || /^[0-9]+$/.test(password)) points = Math.min(points, 2);

    // Map accumulated points to a 1-4 score.
    let score: 1 | 2 | 3 | 4;
    if (points <= 2) score = 1;
    else if (points <= 4) score = 2;
    else if (points <= 5) score = 3;
    else score = 4;

    const meta: Record<number, Omit<PasswordStrength, 'score'>> = {
        1: { label: 'Weak', percent: 25, variant: 'danger' },
        2: { label: 'Fair', percent: 50, variant: 'warning' },
        3: { label: 'Good', percent: 75, variant: 'info' },
        4: { label: 'Strong', percent: 100, variant: 'success' },
    };

    return { score, ...meta[score] };
};
